/**
 * The detail-loss rule: replacing a stored identity value (a serial, a make, a model) with one the stored value
 * already HOLDS, plus more, would delete the extra text ("116556 2019/03" replaced by "116556" deletes "2019/03").
 * Nick, 2026-10-08: that is never proposed; 2026-10-13 (component review): a save that would do it is refused and the
 * person is told to keep the extra text in the component's notes instead.
 *
 * One rule, read by the history loader script (functions/src/scripts/masterData/extraRows.ts) and the `updateGenerator` callable's
 * keep-in-notes step (functions/src/projects/detailLossGuard.ts). The unit-record planner does not read it: it never deletes
 * (every superseded value goes to history).
 * Pure (no I/O). Words are runs of letters/digits/combining marks, compared in NFC; everything else is a separator,
 * and case is ignored. Whole tokens only: "1165567" never holds "116556". LIMIT (named): only a CONTIGUOUS run of the
 * stored value counts as held, so dropping a non-adjacent word ("116556 2019/03 REV B" -> "116556 REV B") is not seen.
 */
import type { UnitComponentType } from './registry';

/**
 * The `updateGenerator` keys (client names of AppSheet columns) the rule applies to: make / model / serial of each
 * component that has a notes box. The server maps each key to its mirror column; both sides read THIS list.
 */
export const DETAIL_GUARDED_FIELDS: Readonly<Record<string, { readonly component: UnitComponentType; readonly label: string }>> = {
  generatorMake: { component: 'generator', label: 'make' },
  generatorModel: { component: 'generator', label: 'model' },
  generatorSerialNumber: { component: 'generator', label: 'serial number' },
  engineMake: { component: 'engine', label: 'make' },
  engineModel: { component: 'engine', label: 'model' },
  engineSerialNumber: { component: 'engine', label: 'serial number' },
  alternatorMake: { component: 'alternator', label: 'make' },
  alternatorModel: { component: 'alternator', label: 'model' },
  alternatorSerialNumber: { component: 'alternator', label: 'serial number' },
  atsMake: { component: 'ats', label: 'make' },
  atsModel: { component: 'ats', label: 'model' },
  atsSerialNumber: { component: 'ats', label: 'serial number' },
  ats2Make: { component: 'ats2', label: 'make' },
  ats2Model: { component: 'ats2', label: 'model' },
  ats2SerialNumber: { component: 'ats2', label: 'serial number' },
};

/** Where the proposed value sits inside the stored value: at its start, its end, in the middle, or only once split tokens are joined. */
export type HeldPosition = 'starts' | 'ends' | 'inside' | 'split';

interface Token {
  readonly text: string;
  readonly upper: string;
  readonly start: number;
  readonly end: number;
}

function tokensOf(s: string): Token[] {
  // Callers pass NFC text (detailLostBy normalises once), so indices stay valid for slicing.
  const out: Token[] = [];
  for (const m of s.matchAll(/[\p{L}\p{N}\p{M}]+/gu)) {
    const start = m.index ?? 0;
    out.push({ text: m[0], upper: m[0].toUpperCase(), start, end: start + m[0].length });
  }
  return out;
}

interface Held {
  readonly position: HeldPosition;
  readonly from: number;
  readonly to: number;
}

/**
 * `part`'s tokens as one contiguous in-order run of `whole`'s tokens, or a run of `whole` that spells the same text once
 * joined ("116 556" holds "116556"). Linear in practice: each start index extends only until the joined run is as long as
 * the target, so a very long stored value cannot make this cubic.
 */
function findHeldRun(whole: Token[], part: Token[]): Held | null {
  if (part.length === 0) return null;
  const joined = part.map((t) => t.upper).join('');
  for (let i = 0; i < whole.length; i++) {
    let acc = '';
    for (let j = i; j < whole.length; j++) {
      acc += whole[j].upper;
      if (acc.length > joined.length) break;
      const len = j - i + 1;
      if (len === part.length) {
        let same = true;
        for (let k = 0; k < len; k++) if (whole[i + k].upper !== part[k].upper) { same = false; break; }
        if (same) return { position: i === 0 ? 'starts' : j === whole.length - 1 ? 'ends' : 'inside', from: i, to: j + 1 };
      }
      if (acc === joined && len !== part.length) return { position: 'split', from: i, to: j + 1 };
    }
  }
  return null;
}

export interface DetailLoss {
  /** The stored text the replacement would delete, as typed in the stored value, in its order ("2019/03"). */
  readonly lost: string;
  readonly position: HeldPosition;
}

/**
 * What replacing `stored` with `proposed` would delete, or null when nothing is lost: the stored value is blank, the
 * two spell the same value, or the proposal is not held inside the stored value (a different value, or one that adds to it).
 *
 * @param {string} stored The value the record holds now.
 * @param {string} proposed The value about to replace it.
 * @return {DetailLoss | null} The text that would be deleted, or null.
 */
export function detailLostBy(stored: string, proposed: string): DetailLoss | null {
  if (typeof stored !== 'string' || typeof proposed !== 'string' || !stored.trim()) return null;
  // NFC both: the same letter typed as one character or as letter + combining mark is one word.
  const text = stored.normalize('NFC');
  const whole = tokensOf(text);
  const held = findHeldRun(whole, tokensOf(proposed.normalize('NFC')));
  if (!held) return null;
  const rest = [...whole.slice(0, held.from), ...whole.slice(held.to)];
  if (rest.length === 0) return null; // the same value
  const before = whole.slice(0, held.from);
  const after = whole.slice(held.to);
  const span = (ts: Token[]): string => (ts.length ? text.slice(ts[0].start, ts[ts.length - 1].end) : '');
  return { lost: [span(before), span(after)].filter(Boolean).join(' '), position: held.position };
}

/**
 * Where `stored` holds `proposed` (as a whole-word run) whether or not anything is left over, else null. The loader
 * script's classifier needs this form: "the record already holds this value".
 *
 * @param {string} stored The value the record holds now.
 * @param {string} proposed The value being compared.
 * @return {HeldPosition | null} Where it sits, or null.
 */
export function holdsValue(stored: string, proposed: string): HeldPosition | null {
  if (typeof stored !== 'string' || typeof proposed !== 'string') return null;
  return findHeldRun(tokensOf(stored.normalize('NFC')), tokensOf(proposed.normalize('NFC')))?.position ?? null;
}

/**
 * Do these notes already keep the lost text? Deliberately the strictest test: ONE note segment (notes are written as
 * `a; b`, one segment per kept text; a newline also separates) must spell exactly the lost words. Words found inside a
 * longer sentence, spread over two segments or matched across a separator are NOT kept: the caller keeps the text again,
 * which costs an occasional duplicate note, never a silent loss.
 *
 * @param {string | null | undefined} notes The component's stored notes.
 * @param {string} lost The text `detailLostBy` reported.
 * @return {boolean} True when saving the replacement loses nothing.
 */
export function notesHold(notes: string | null | undefined, lost: string): boolean {
  const want = tokensOf(lost.normalize('NFC'));
  if (want.length === 0) return true;
  if (typeof notes !== 'string') return false;
  for (const segment of notes.normalize('NFC').split(/[;\n]+/)) {
    const have = tokensOf(segment);
    if (have.length === want.length && have.every((t, k) => t.upper === want[k].upper)) return true;
  }
  return false;
}

/** Longest make / model / serial value the rule will judge (a real one is under 100 characters). */
export const MAX_GUARDED_VALUE_LENGTH = 500;

/** A positive and a negative control for the rule; callers that count with it print both. */
export function detailLossControl(): { ok: boolean; positive: string | null; negative: string | null } {
  const positive = detailLostBy('116556 2019/03', '116556')?.lost ?? null;
  const negative = detailLostBy('1165567', '116556')?.lost ?? null;
  return { ok: positive === '2019/03' && negative === null, positive, negative };
}
